import { createAction } from '@reduxjs/toolkit';
import { DocumentResourceType, ResourceSection } from 'data/documents';
import { SpaceTab } from 'data/space-tabs';
import { TemplateState } from '../api';
import { ProjectTemplateTask } from '../projects';
import { DocumentTemplate } from './types';

interface BaseActionPayload {
  handleFailure?(): void;
  handleAlways?(): void;
}

export interface CreateDocumentTemplatePayload extends BaseActionPayload {
  spaceId?: number;
  state: TemplateState;
  templateName: string;
  handleSuccess?(tabId: number): void;
}
export const createDocumentTemplateAction =
  createAction<CreateDocumentTemplatePayload>(
    '@@TEMPLATES/DOCUMENT/createDocumentTemplateAction',
  );

export interface UpdateDocumentTemplateAction extends BaseActionPayload {
  spaceId?: number;
  spaceTab?: SpaceTab;
  sync?: boolean;
  template: AtLeast<DocumentTemplate, 'templateId'>;
  handleSuccess?(template: DocumentTemplate): void;
}
export const updateDocumentTemplateAction =
  createAction<UpdateDocumentTemplateAction>(
    '@@TEMPLATES/DOCUMENT/updateDocumentTemplateAction',
  );

export interface UpdateDocumentTemplateContentAction extends BaseActionPayload {
  sectionId: number;
  content: string;
  templateId: number;
  handleSuccess?(template: DocumentTemplate): void;
}
export const updateDocumentTemplateContentAction =
  createAction<UpdateDocumentTemplateContentAction>(
    '@@TEMPLATES/DOCUMENT/updateDocumentTemplateContentAction',
  );

export interface EmbedDocumentTemplateActionPayload extends BaseActionPayload {
  spaceId: number;
  url: string;
  handleSuccess?(tabId: number): void;
}
export const embedDocumentTemplateAction =
  createAction<EmbedDocumentTemplateActionPayload>(
    '@@TEMPLATES/DOCUMENT/embedDocumentTemplateAction',
  );

export interface AddDocumentFromTemplatePayload extends BaseActionPayload {
  spaceId: number;
  template: AtLeast<DocumentTemplate, 'templateId' | 'templateName'>;
  handleSuccess?(tabId: number): void;
}
export const addDocumentFromTemplateAction =
  createAction<AddDocumentFromTemplatePayload>(
    '@@TEMPLATES/DOCUMENT/addDocumentFromTemplateAction',
  );

export interface FetchDocumentTemplateAction extends BaseActionPayload {
  templateId: number;
}
export const fetchDocumentTemplateAction =
  createAction<FetchDocumentTemplateAction>(
    '@@TEMPLATES/DOCUMENT/fetchDocumentTemplateAction',
  );

export interface CreateDocumentTaskPayload extends BaseActionPayload {
  docId: number;
  resourceSectionId?: number;
  resourceType?: DocumentResourceType;
  task: Partial<ProjectTemplateTask>;
}
export const createDocumentTemplateTaskAction =
  createAction<CreateDocumentTaskPayload>(
    '@@TEMPLATES/DOCUMENT/createDocumentTemplateTaskAction',
  );

export interface CreateDocumentHighlightTaskPayload extends BaseActionPayload {
  docId: number;
  resourceSectionId: number;
  sectionId: number;
  task: AtLeast<ProjectTemplateTask, 'taskId'>;
}
export const createDocumentTemplateHighlightTaskAction =
  createAction<CreateDocumentHighlightTaskPayload>(
    '@@TEMPLATES/DOCUMENT/createDocumentTemplateHighlightTaskAction',
  );

export interface UpdateDocumentTaskPayload extends BaseActionPayload {
  docId: number;
  resourceSectionId: number;
  task: AtLeast<ProjectTemplateTask, 'taskId'>;
}
export const updateDocumentTemplateTaskAction =
  createAction<UpdateDocumentTaskPayload>(
    '@@TEMPLATES/DOCUMENT/updateDocumentTemplateTaskAction',
  );

export interface DeleteDocumentTaskPayload extends BaseActionPayload {
  docId: number;
  resourceSectionId: number;
  taskId: number;
}
export const deleteDocumentTemplateTaskAction =
  createAction<DeleteDocumentTaskPayload>(
    '@@TEMPLATES/DOCUMENT/deleteDocumentTemplateTaskAction',
  );

export interface CreateDocumentResourceSectionPayload
  extends BaseActionPayload {
  docId: number;
  resourceSectionId?: number;
  resourceSectionName: string;
  resourceType: DocumentResourceType;
  sectionId?: number;
}
export const createDocumentTemplateResourceSectionAction =
  createAction<CreateDocumentResourceSectionPayload>(
    '@@TEMPLATES/DOCUMENT/createDocumentTemplateResourceSectionAction',
  );

export interface UpdateDocumentResourceSectionPayload
  extends BaseActionPayload {
  docId: number;
  resource: AtLeast<
    ResourceSection<Partial<ProjectTemplateTask>>,
    'resourceSectionId'
  >;
}
export const updateDocumentTemplateResourceSectionAction =
  createAction<UpdateDocumentResourceSectionPayload>(
    '@@TEMPLATES/DOCUMENT/updateDocumentTemplateResourceSectionAction',
  );

export interface CreateDocumentTemplatePrivateSection
  extends BaseActionPayload {
  docId: number;
}
export const createDocumentTemplatePrivateSectionAction =
  createAction<CreateDocumentTemplatePrivateSection>(
    '@@TEMPLATES/DOCUMENT/createDocumentTemplatePrivateSectionAction',
  );
